import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, ChevronDown, CalendarDays, CalendarOff } from 'lucide-react';
import { logger } from '@/lib/logger';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { format, startOfWeek, addDays } from 'date-fns';
import { pl } from 'date-fns/locale';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import { useClosuresInRange } from '@/hooks/useClosures';
import {
  getAttendanceStatus,
  ATTENDANCE_STATUS
} from '@/hooks/useAttendanceStatus';

export default function WeeklySummary({
  childId,
  branchId,
  criteriaSchema,
  selectedDate,
  onDateChange,
  branchBadges = {}
}) {
  const [expanded, setExpanded] = useState(false);
  const [weekStart, setWeekStart] = useState(() =>
    startOfWeek(new Date(selectedDate), { weekStartsOn: 1 })
  );
  const [updates, setUpdates] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(false);

  // Sync weekStart when selectedDate changes
  useEffect(() => {
    const newWeekStart = startOfWeek(new Date(selectedDate), { weekStartsOn: 1 });
    if (newWeekStart.getTime() !== weekStart.getTime()) {
      setWeekStart(newWeekStart);
    }
  }, [selectedDate]);

  // Get closures for the week
  const weekStartStr = format(weekStart, 'yyyy-MM-dd');
  const weekEndStr = format(addDays(weekStart, 4), 'yyyy-MM-dd');
  const { data: weekClosures } = useClosuresInRange(branchId, weekStartStr, weekEndStr);
  const closedDates = new Set(weekClosures?.map(c => c.closure_date) || []);
  const closureMap = Object.fromEntries(weekClosures?.map(c => [c.closure_date, c]) || []);

  // Load updates and absences when week changes
  useEffect(() => {
    if (!childId || !expanded) return;

    const loadWeekData = async () => {
      setLoading(true);
      try {
        const weekEnd = format(addDays(weekStart, 4), 'yyyy-MM-dd');
        const weekStartFormatted = format(weekStart, 'yyyy-MM-dd');

        const [updatesRes, absencesRes] = await Promise.all([
          supabase
            .from('daily_updates')
            .select('*')
            .eq('child_id', childId)
            .gte('date', weekStartFormatted)
            .lte('date', weekEnd)
            .order('date', { ascending: true }),
          supabase
            .from('absences')
            .select('*')
            .eq('child_id', childId)
            .gte('absence_date', weekStartFormatted)
            .lte('absence_date', weekEnd)
        ]);

        setUpdates(updatesRes.data || []);
        setAbsences(absencesRes.data || []);
      } catch (e) {
        logger.error('Failed to load week data:', e);
      } finally {
        setLoading(false);
      }
    };

    loadWeekData();
  }, [childId, weekStart, expanded]);

  // Week navigation
  const goToPreviousWeek = () => {
    const newWeekStart = addDays(weekStart, -7);
    setWeekStart(newWeekStart);
    if (onDateChange) {
      onDateChange(format(newWeekStart, 'yyyy-MM-dd'));
    }
  };

  const goToNextWeek = () => {
    const newWeekStart = addDays(weekStart, 7);
    const today = new Date();
    if (newWeekStart <= today) {
      setWeekStart(newWeekStart);
      if (onDateChange) {
        onDateChange(format(newWeekStart, 'yyyy-MM-dd'));
      }
    }
  };

  const isCurrentWeek = () => {
    const currentWeekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
    return weekStart.getTime() === currentWeekStart.getTime();
  };

  // Get weekdays (Mon-Fri)
  const getWeekDays = () => {
    return [0, 1, 2, 3, 4].map(i => addDays(weekStart, i));
  };

  // Build maps for quick lookup
  const updatesByDate = {};
  updates.forEach(u => {
    updatesByDate[u.date] = u;
  });

  const absencesByDate = {};
  absences.forEach(a => {
    absencesByDate[a.absence_date] = a;
  });

  const today = format(new Date(), 'yyyy-MM-dd');
  const weekDays = getWeekDays();

  // Legacy badge icons fallback
  const legacyBadgeIcons = {
    star: '⭐', helper: '🤝', creative: '🎨', heart: '❤️',
    trophy: '🏆', friend: '🌟', book: '📚', music: '🎵', brave: '🦁'
  };

  const getBadgeIcon = (badgeId) => {
    if (branchBadges[badgeId]) {
      return branchBadges[badgeId].emoji;
    }
    return legacyBadgeIcons[badgeId] || '🏅';
  };

  // Check if any day has badges or notes
  const hasBadges = updates.some(u => u.badges?.length > 0);
  const hasNotes = updates.some(u => u.notes);

  return (
    <div className="border-t border-slate-200 pt-4">
      {/* Header - always visible */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => setExpanded(!expanded)}
        onKeyDown={(e) => e.key === 'Enter' && setExpanded(!expanded)}
        className="w-full flex items-center justify-between text-left hover:bg-slate-50 rounded-lg p-2 -m-2 transition-colors cursor-pointer"
      >
        <div className="flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-blue-500" />
          <span className="font-medium text-slate-700 text-sm">Podsumowanie tygodnia</span>
        </div>
        <div className="flex items-center gap-2">
          {/* Week navigation */}
          <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={goToPreviousWeek}
            >
              <ChevronLeft className="w-3 h-3" />
            </Button>
            <span className="text-xs text-slate-500 w-28 text-center">
              {format(weekStart, 'd', { locale: pl })} - {format(addDays(weekStart, 4), 'd MMM', { locale: pl })}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={goToNextWeek}
              disabled={isCurrentWeek()}
            >
              <ChevronRight className="w-3 h-3" />
            </Button>
          </div>
          <ChevronDown
            className={cn(
              "w-4 h-4 text-slate-400 transition-transform duration-300 ease-out",
              expanded && "rotate-180"
            )}
          />
        </div>
      </div>

      {/* Animated expandable content */}
      <div
        className={cn(
          "grid transition-all duration-300 ease-out",
          expanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        )}
      >
        <div className="overflow-hidden">
          <div className="mt-4">
            {loading ? (
              <div className="text-center py-4 text-slate-400 text-sm">
                Ładowanie...
              </div>
            ) : (
              <div className="space-y-3">
                {/* Weekly Grid Table */}
                <div className="overflow-x-auto -mx-2 px-2">
                  <table className="w-full min-w-[300px]">
                    <thead>
                      <tr>
                        <th className="text-left text-xs font-medium text-slate-500 pb-2 pr-2 w-20"></th>
                        {weekDays.map(day => {
                          const dateStr = format(day, 'yyyy-MM-dd');
                          const isToday = dateStr === today;
                          const isFuture = day > new Date();
                          const isClosed = closedDates.has(dateStr);
                          return (
                            <th
                              key={dateStr}
                              className={cn(
                                "text-center text-xs font-medium pb-2 px-1 min-w-[44px]",
                                isClosed ? "text-red-400 bg-red-50 rounded-t" :
                                isToday ? "text-blue-600" :
                                isFuture ? "text-slate-300" : "text-slate-500"
                              )}
                              title={isClosed ? closureMap[dateStr]?.title : undefined}
                            >
                              <div>{format(day, 'EEE', { locale: pl })}</div>
                              <div className={isToday ? 'font-bold' : ''}>{format(day, 'd')}</div>
                              {isClosed && <CalendarOff className="w-3 h-3 mx-auto text-red-400" />}
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {/* Attendance row */}
                      <tr className="border-t border-slate-100">
                        <td className="text-xs font-medium text-slate-600 py-2 pr-2 whitespace-nowrap">
                          📋 Obecność
                        </td>
                        {weekDays.map(day => {
                          const dateStr = format(day, 'yyyy-MM-dd');
                          const isClosed = closedDates.has(dateStr);

                          if (isClosed) {
                            return (
                              <td key={dateStr} className="text-center py-2 px-1 bg-red-50">
                                <span className="text-xs text-red-400">-</span>
                              </td>
                            );
                          }

                          const update = updatesByDate[dateStr];
                          const absence = absencesByDate[dateStr];
                          const status = getAttendanceStatus(dateStr, update, absence);

                          let mark = '·';
                          let bgClass = '';
                          let textClass = 'text-slate-300';

                          if (status === null) {
                            mark = '–';
                          } else if (status === ATTENDANCE_STATUS.PRESENT) {
                            mark = '✓';
                            bgClass = 'bg-green-50';
                            textClass = 'text-green-600 font-bold';
                          } else if (status === ATTENDANCE_STATUS.ABSENT_PLANNED) {
                            mark = '✗';
                            bgClass = 'bg-amber-50';
                            textClass = 'text-amber-600 font-bold';
                          } else if (status === ATTENDANCE_STATUS.ABSENT_UNPLANNED) {
                            mark = '✗';
                            bgClass = 'bg-red-50';
                            textClass = 'text-red-500 font-bold';
                          }

                          return (
                            <td key={dateStr} className={cn("text-center py-2 px-1", bgClass)}>
                              <span className={cn("text-lg", textClass)}>{mark}</span>
                            </td>
                          );
                        })}
                      </tr>

                      {/* Criteria rows */}
                      {criteriaSchema.map(criterion => (
                        <tr key={criterion.key} className="border-t border-slate-100">
                          <td className="text-xs font-medium text-slate-600 py-2 pr-2 whitespace-nowrap">
                            {criterion.icon} {criterion.label}
                          </td>
                          {weekDays.map(day => {
                            const dateStr = format(day, 'yyyy-MM-dd');
                            const isClosed = closedDates.has(dateStr);

                            if (isClosed) {
                              return (
                                <td key={dateStr} className="text-center py-2 px-1 bg-red-50">
                                  <span className="text-xs text-red-400">-</span>
                                </td>
                              );
                            }

                            const update = updatesByDate[dateStr];
                            const value = update?.criteria_values?.[criterion.key];
                            const isFuture = day > new Date();

                            let emoji = '·';
                            let bgClass = '';
                            if (value === 'positive') {
                              emoji = '😊';
                              bgClass = 'bg-green-50';
                            } else if (value === 'neutral') {
                              emoji = '😐';
                              bgClass = 'bg-yellow-50';
                            } else if (value === 'negative') {
                              emoji = '😢';
                              bgClass = 'bg-red-50';
                            }

                            return (
                              <td
                                key={dateStr}
                                className={cn("text-center py-2 px-1", bgClass, isFuture && "opacity-30")}
                              >
                                <span className="text-xl">{emoji}</span>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Legend */}
                <div className="flex justify-center flex-wrap gap-2 text-xs text-slate-500">
                  <span className="text-green-600">✓ Obecny</span>
                  <span className="text-amber-600">✗ Plan.</span>
                  <span className="text-red-500">✗ Nieob.</span>
                  <span>😊 Super</span>
                  <span>😐 OK</span>
                  <span>😢 Słabo</span>
                </div>

                {/* Badges section */}
                {hasBadges && (
                  <div className="p-2 bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200 rounded-lg">
                    <p className="text-xs font-medium text-yellow-900 mb-1">Odznaki</p>
                    <div className="flex flex-wrap gap-1">
                      {updates.filter(u => u.badges?.length > 0).map(u => (
                        <div key={u.id} className="flex items-center gap-0.5 bg-white/60 px-1.5 py-0.5 rounded text-xs">
                          <span className="text-slate-500">{format(new Date(u.date), 'EEE', { locale: pl })}:</span>
                          {u.badges.map((b, i) => (
                            <span key={i} className="text-base">{getBadgeIcon(b)}</span>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Notes section */}
                {hasNotes && (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-slate-700">Notatki</p>
                    {updates.filter(u => u.notes).map(u => (
                      <div key={u.id} className="p-2 bg-blue-50 border border-blue-100 rounded text-xs">
                        <span className="font-medium text-blue-700">
                          {format(new Date(u.date), 'EEE', { locale: pl })}:
                        </span>{' '}
                        <span className="text-slate-700">{u.notes}</span>
                      </div>
                    ))}
                  </div>
                )}

                {isCurrentWeek() && (
                  <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 text-xs">
                    Ten tydzień
                  </Badge>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
